import React, { useState, useEffect } from "react";
import "@/App.css";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import About from "@/components/About";
import Research from "@/components/Research";
import ResearchVisualizations from "@/components/ResearchVisualizations";
import Publications from "@/components/Publications";
import Experience from "@/components/Experience";
import Certifications from "@/components/Certifications";
import SkillsVisualization from "@/components/SkillsVisualization";
import Achievements from "@/components/Achievements";
import Testimonials from "@/components/Testimonials";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import AIResearchAssistant from "@/components/AIResearchAssistant";
import ScrollProgressBar from "@/components/ScrollProgressBar";
import ScrollToTop from "@/components/ScrollToTop";
import CustomCursor from "@/components/CustomCursor";
import ParticleNetwork from "@/components/ParticleNetwork";
import portfolioApi from "@/api/portfolioApi";

function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    personalInfo: null,
    education: null,
    researchProjects: [],
    publications: [],
    experience: [],
    certifications: [],
    skills: null,
    achievements: [],
    testimonials: []
  });

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        const [
          personalInfo,
          education,
          researchProjects,
          publications,
          experience,
          certifications,
          skills,
          achievements,
          testimonials
        ] = await Promise.all([
          portfolioApi.getPersonalInfo(),
          portfolioApi.getEducation(),
          portfolioApi.getResearchProjects(),
          portfolioApi.getPublications(),
          portfolioApi.getExperience(),
          portfolioApi.getCertifications(),
          portfolioApi.getSkills(),
          portfolioApi.getAchievements(),
          portfolioApi.getTestimonials()
        ]);

        setData({
          personalInfo,
          education,
          researchProjects,
          publications,
          experience,
          certifications,
          skills,
          achievements,
          testimonials
        });
      } catch (err) {
        console.error("Error fetching portfolio data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-blue-900 mx-auto mb-4"></div>
            <div className="absolute inset-0 animate-ping rounded-full h-20 w-20 border-2 border-blue-300 mx-auto opacity-20"></div>
          </div>
          <p className="text-gray-700 text-lg font-semibold animate-pulse">Loading Portfolio...</p>
          <p className="text-gray-500 text-sm mt-2">Initializing server (cold start). This may take a few seconds...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md">
          <div className="text-red-600 text-6xl mb-4">⚠</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Server Warming Up</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-all transform hover:scale-105 shadow-lg"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App relative custom-cursor-enabled">
      {/* Animated Background - Particle Network Only */}
      <ParticleNetwork />
      
      {/* Custom Cursor */}
      <CustomCursor />
      
      {/* Scroll Progress Bar */}
      <ScrollProgressBar />
      
      {/* Main Content */}
      <Navbar personalInfo={data.personalInfo} />
      <Hero personalInfo={data.personalInfo} />
      <About education={data.education} />
      <Research researchProjects={data.researchProjects} />
      <ResearchVisualizations />
      <Publications publications={data.publications} />
      <Experience experience={data.experience} />
      <Certifications certifications={data.certifications} />
      <SkillsVisualization skills={data.skills} />
      <Achievements achievements={data.achievements} />
      <Testimonials testimonials={data.testimonials} />
      <Contact personalInfo={data.personalInfo} />
      <Footer personalInfo={data.personalInfo} />
      
      {/* Floating Features */}
      <AIResearchAssistant />
      <ScrollToTop />
    </div>
  );
}

export default App;
